import React from 'react';
import { logError } from '@utils/usefulFuncs';

const initialState = {
  turns: [],
  isResumed: false,
  resumedFromId: null,
  resumedFromCreatedAt: null,
};

const defaultContextValue = {
  state: initialState,
  startTurn: () => logError('startTurn not initialized'),
  completeTurn: () => logError('completeTurn not initialized'),
  failTurn: () => logError('failTurn not initialized'),
  resetSession: () => logError('resetSession not initialized'),
  resumeSession: () => logError('resumeSession not initialized'),
};

const ACTIONS = {
  START_TURN: 'START_TURN',
  COMPLETE_TURN: 'COMPLETE_TURN',
  FAIL_TURN: 'FAIL_TURN',
  RESET_SESSION: 'RESET_SESSION',
  RESUME_SESSION: 'RESUME_SESSION',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.START_TURN: {
      const { turnId, question, omittedRequest, daaRecordId } = action.payload;
      const newTurn = {
        turnId,
        question,
        omittedRequest: !!omittedRequest,
        daaRecordId,
        status: 'loading',
        response: null,
        error: null,
      };
      return { ...state, turns: [...state.turns, newTurn] };
    }
    case ACTIONS.COMPLETE_TURN: {
      const { turnId, response } = action.payload;
      return {
        ...state,
        turns: state.turns.map((turn) =>
          turn.turnId === turnId ? { ...turn, status: 'completed', response, error: null } : turn
        ),
      };
    }
    case ACTIONS.FAIL_TURN: {
      const { turnId, error } = action.payload;
      return {
        ...state,
        turns: state.turns.map((turn) =>
          turn.turnId === turnId ? { ...turn, status: 'error', error, response: null } : turn
        ),
      };
    }
    case ACTIONS.RESET_SESSION: {
      return initialState;
    }
    case ACTIONS.RESUME_SESSION: {
      const { turn, resumedFromId, resumedFromCreatedAt } = action.payload;
      return {
        ...initialState,
        turns: [turn],
        isResumed: true,
        resumedFromId,
        resumedFromCreatedAt,
      };
    }
    default:
      return state;
  }
};

export const SessionContext = React.createContext(defaultContextValue);

export const SessionProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const startTurn = React.useCallback((payload) => {
    dispatch({ type: ACTIONS.START_TURN, payload });
  }, []);

  const completeTurn = React.useCallback((payload) => {
    dispatch({ type: ACTIONS.COMPLETE_TURN, payload });
  }, []);

  const failTurn = React.useCallback((payload) => {
    dispatch({ type: ACTIONS.FAIL_TURN, payload });
  }, []);

  const resetSession = React.useCallback(() => {
    dispatch({ type: ACTIONS.RESET_SESSION });
  }, []);

  const resumeSession = React.useCallback((payload) => {
    dispatch({ type: ACTIONS.RESUME_SESSION, payload });
  }, []);

  const value = React.useMemo(
    () => ({ state, startTurn, completeTurn, failTurn, resetSession, resumeSession }),
    [state, startTurn, completeTurn, failTurn, resetSession, resumeSession]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const selectIsSessionActive = (state) => state.turns.length > 0;

export const selectLatestCompletedTurn = (state) => {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    if (state.turns[i].status === 'completed') return state.turns[i];
  }
  return null;
};

export const selectActiveLoadingTurn = (state) => {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    if (state.turns[i].status === 'loading') return state.turns[i];
  }
  return null;
};

export const selectContinueFromRecordId = (state) => {
  const latest = selectLatestCompletedTurn(state);
  return latest ? latest.daaRecordId : null;
};

export const selectIsTurnInFlight = (state) => !!selectActiveLoadingTurn(state);

export const selectIsResumed = (state) => !!state.isResumed;
export const selectResumedFromId = (state) => state.resumedFromId;
export const selectResumedFromCreatedAt = (state) => state.resumedFromCreatedAt;
